import { API_BASE, getToken } from "./api.js";
import { tuneOpusForVoice } from "./sdp.js";

/**
 * Live commentary over WebRTC.
 *
 * The scorer speaks and a listener hears it in about the time it takes sound to
 * cross a ground, instead of the several seconds an upload takes. That is the
 * whole point: commentary that arrives after the next ball has been bowled is
 * not commentary.
 *
 * Shape of it:
 *
 *   - The match WebSocket carries the handshake. It is already open, already
 *     authenticated and already reconnects — opening a second socket to say the
 *     same things would mean two of everything to keep alive.
 *   - The audio is peer to peer. A Worker cannot forward media, so the scorer
 *     sends one stream per listener. That is why the room caps listeners.
 *   - Nothing here replaces the recorded clips. The scorer's phone still
 *     records and uploads every press, exactly as before. Live is a layer on
 *     top for whoever can hold a connection; the clip is the record, it reaches
 *     everyone, and it is what survives being offline.
 *
 * This module deliberately knows nothing about how messages travel. It is given
 * a `send` and is fed inbound frames, which keeps the socket owned by the
 * screen that opened it and keeps this testable without a browser.
 */

export type Transport = (msg: Record<string, unknown>) => void;

export interface IceConfig {
  iceServers: RTCIceServer[];
  /** False when there is no TURN relay, so strict-NAT peers will not connect. */
  relay: boolean;
}

let cachedIce: IceConfig | null = null;

/**
 * Fetch ICE servers once per session.
 *
 * A failure here is not fatal. Plain STUN still connects everyone on the same
 * wifi, which at a club ground is most people.
 */
export async function iceConfig(): Promise<IceConfig> {
  if (cachedIce) return cachedIce;
  const fallback: IceConfig = {
    iceServers: [{ urls: ["stun:stun.cloudflare.com:3478", "stun:stun.l.google.com:19302"] }],
    relay: false,
  };
  try {
    const token = getToken();
    const r = await fetch(`${API_BASE}/api/v1/rtc/ice`, {
      credentials: "same-origin",
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) return fallback;
    const d = (await r.json()) as Partial<IceConfig>;
    cachedIce = {
      iceServers: d.iceServers?.length ? d.iceServers : fallback.iceServers,
      relay: d.relay === true,
    };
    return cachedIce;
  } catch {
    return fallback;
  }
}

export function supported(): boolean {
  return typeof RTCPeerConnection !== "undefined"
    && typeof navigator !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia;
}

/* ── the scorer's side ────────────────────────────────────────────────────── */

interface Wire {
  pc: RTCPeerConnection;
  /**
   * Candidates that arrived before the answer did.
   *
   * addIceCandidate throws if there is no remote description yet, and on a
   * fast connection the first candidates routinely beat the answer back.
   */
  pending: RTCIceCandidateInit[];
  answered: boolean;
}

export interface BroadcastStatus {
  /** Listeners with a working media path right now. */
  connected: number;
  /** Listeners who asked for audio, connected or still trying. */
  wanted: number;
  /** True once at least one listener failed and TURN was not available. */
  blocked: boolean;
}

export class Broadcaster {
  private peers = new Map<string, Wire>();
  private track: MediaStreamTrack | null = null;
  private stream: MediaStream | null = null;
  private ice: IceConfig = { iceServers: [], relay: false };
  private onStatus: (s: BroadcastStatus) => void;
  private send: Transport;
  private blocked = false;
  private closed = false;

  constructor(send: Transport, onStatus: (s: BroadcastStatus) => void) {
    this.send = send;
    this.onStatus = onStatus;
  }

  async init(stream: MediaStream): Promise<void> {
    this.ice = await iceConfig();
    this.stream = stream;
    this.track = stream.getAudioTracks()[0] ?? null;
    // Silent until the button is held. The connection stays up between presses
    // so that pressing it costs nothing but flipping this flag — renegotiating
    // per press would put a round trip in front of the first word.
    if (this.track) this.track.enabled = false;
  }

  /** Hold to talk. */
  setTalking(on: boolean): void {
    if (this.track) this.track.enabled = on;
    this.send({ type: "TALK", on });
  }

  /** The room's list of listeners who want audio. */
  async setListeners(peerIds: string[]): Promise<void> {
    if (this.closed) return;
    for (const id of peerIds) {
      if (!this.peers.has(id)) await this.offerTo(id);
    }
    for (const id of [...this.peers.keys()]) {
      if (!peerIds.includes(id)) this.drop(id);
    }
    this.report();
  }

  async handle(msg: Record<string, unknown>): Promise<void> {
    const from = typeof msg["from"] === "string" ? (msg["from"] as string) : null;
    switch (msg["type"]) {
      case "RTC_PEERS":
        await this.setListeners(Array.isArray(msg["peers"]) ? (msg["peers"] as string[]) : []);
        return;
      case "RTC_GONE":
        if (typeof msg["peerId"] === "string") { this.drop(msg["peerId"] as string); this.report(); }
        return;
      case "RTC_ANSWER": {
        if (!from) return;
        const w = this.peers.get(from);
        if (!w) return;
        try {
          await w.pc.setRemoteDescription({ type: "answer", sdp: String(msg["sdp"]) });
          w.answered = true;
          for (const c of w.pending.splice(0)) {
            await w.pc.addIceCandidate(c).catch(() => { /* stale candidate */ });
          }
        } catch (e) {
          console.error("rtc: answer rejected", String(e));
          this.drop(from);
        }
        return;
      }
      case "RTC_ICE": {
        if (!from) return;
        const w = this.peers.get(from);
        const cand = msg["candidate"] as RTCIceCandidateInit | undefined;
        if (!w || !cand) return;
        if (!w.answered) w.pending.push(cand);
        else await w.pc.addIceCandidate(cand).catch(() => { /* stale candidate */ });
        return;
      }
      case "RTC_BYE":
        if (from) { this.drop(from); this.report(); }
        return;
    }
  }

  private async offerTo(peerId: string): Promise<void> {
    if (!this.track || !this.stream) return;
    const pc = new RTCPeerConnection({ iceServers: this.ice.iceServers });
    const wire: Wire = { pc, pending: [], answered: false };
    this.peers.set(peerId, wire);

    pc.addTrack(this.track, this.stream);

    // One direction only. Without this the browser offers to receive audio too
    // and asks the listener for a microphone it has no reason to want.
    for (const t of pc.getTransceivers()) t.direction = "sendonly";

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.send({ type: "RTC_ICE", to: peerId, candidate: e.candidate.toJSON() });
      }
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "failed") {
        // Almost always strict NAT on both ends with no relay to fall back on.
        if (!this.ice.relay) this.blocked = true;
        this.drop(peerId);
      }
      this.report();
    };

    try {
      const offer = await pc.createOffer();
      const sdp = tuneOpusForVoice(offer.sdp ?? "");
      await pc.setLocalDescription({ type: "offer", sdp });
      this.send({ type: "RTC_OFFER", to: peerId, sdp });
      await this.limitBitrate(pc);
    } catch (e) {
      console.error("rtc: could not offer", String(e));
      this.drop(peerId);
    }
  }

  /**
   * A ceiling the SDP cannot set on its own.
   *
   * maxaveragebitrate is a hint to the encoder; this is the transport refusing
   * to carry more. Both are wanted — the scorer is paying for this uplink N
   * times over.
   */
  private async limitBitrate(pc: RTCPeerConnection): Promise<void> {
    for (const sender of pc.getSenders()) {
      if (sender.track?.kind !== "audio") continue;
      const p = sender.getParameters();
      if (!p.encodings || p.encodings.length === 0) p.encodings = [{}];
      p.encodings[0]!.maxBitrate = 28_000;
      try { await sender.setParameters(p); } catch { /* Safari, sometimes */ }
    }
  }

  private drop(peerId: string): void {
    const w = this.peers.get(peerId);
    if (!w) return;
    try { w.pc.close(); } catch { /* already closed */ }
    this.peers.delete(peerId);
  }

  private report(): void {
    let connected = 0;
    for (const w of this.peers.values()) {
      if (w.pc.connectionState === "connected") connected++;
    }
    this.onStatus({ connected, wanted: this.peers.size, blocked: this.blocked });
  }

  close(): void {
    this.closed = true;
    for (const id of [...this.peers.keys()]) this.drop(id);
    if (this.track) this.track.enabled = false;
    this.report();
  }
}

/* ── the spectator's side ─────────────────────────────────────────────────── */

export type ListenState = "off" | "waiting" | "live" | "failed";

/**
 * Receive the scorer's voice.
 *
 * Answers offers, never makes them, and never asks for a microphone — a
 * listener is listening.
 */
export class Listener {
  private pc: RTCPeerConnection | null = null;
  private pending: RTCIceCandidateInit[] = [];
  private described = false;
  private el: HTMLAudioElement;
  private send: Transport;
  private onState: (s: ListenState, detail?: string) => void;
  private ice: IceConfig = { iceServers: [], relay: false };
  private scorerPeer: string | null = null;
  private on = false;

  constructor(send: Transport, el: HTMLAudioElement, onState: (s: ListenState, d?: string) => void) {
    this.send = send;
    this.el = el;
    this.onState = onState;
  }

  /**
   * Turn it on. Must be called from a tap.
   *
   * Autoplay rules mean a stream attached without a user gesture is muted, and
   * a listener who tapped Listen and heard silence would reasonably conclude
   * the feature is broken.
   */
  async start(): Promise<void> {
    if (this.on) return;
    this.on = true;
    this.ice = await iceConfig();
    this.send({ type: "RTC_READY" });
    this.onState("waiting");
  }

  stop(): void {
    if (!this.on) return;
    this.on = false;
    if (this.scorerPeer) this.send({ type: "RTC_BYE", to: this.scorerPeer });
    this.send({ type: "RTC_STOP" });
    this.teardown();
    this.onState("off");
  }

  async handle(msg: Record<string, unknown>): Promise<void> {
    if (!this.on) return;
    const from = typeof msg["from"] === "string" ? (msg["from"] as string) : null;

    if (msg["type"] === "RTC_OFFER" && from) {
      this.scorerPeer = from;
      await this.answer(from, String(msg["sdp"]));
      return;
    }
    if (msg["type"] === "RTC_ICE" && from) {
      const cand = msg["candidate"] as RTCIceCandidateInit | undefined;
      if (!cand) return;
      if (!this.described || !this.pc) this.pending.push(cand);
      else await this.pc.addIceCandidate(cand).catch(() => { /* stale candidate */ });
      return;
    }
    if (msg["type"] === "RTC_BYE") {
      this.teardown();
      // The scorer went away, not the listener. Stay opted in so that when
      // they come back the room offers again without another tap.
      this.onState("waiting");
    }
  }

  private async answer(peerId: string, sdp: string): Promise<void> {
    this.teardown();
    const pc = new RTCPeerConnection({ iceServers: this.ice.iceServers });
    this.pc = pc;

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      if (!stream) return;
      this.el.srcObject = stream;
      void this.el.play().catch(() => {
        this.onState("failed", "Tap Listen again to allow sound.");
      });
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.send({ type: "RTC_ICE", to: peerId, candidate: e.candidate.toJSON() });
      }
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") this.onState("live");
      else if (s === "failed") {
        this.onState("failed", this.ice.relay
          ? "Could not reach the scorer's phone."
          : "Could not connect on this network. You will still get the clips.");
      } else if (s === "disconnected") this.onState("waiting");
    };

    try {
      await pc.setRemoteDescription({ type: "offer", sdp });
      this.described = true;
      for (const c of this.pending.splice(0)) {
        await pc.addIceCandidate(c).catch(() => { /* stale candidate */ });
      }
      const ans = await pc.createAnswer();
      const tuned = tuneOpusForVoice(ans.sdp ?? "");
      await pc.setLocalDescription({ type: "answer", sdp: tuned });
      this.send({ type: "RTC_ANSWER", to: peerId, sdp: tuned });
    } catch (e) {
      console.error("rtc: could not answer", String(e));
      this.onState("failed", "Could not start listening.");
      this.teardown();
    }
  }

  private teardown(): void {
    this.described = false;
    this.pending = [];
    try { this.pc?.close(); } catch { /* already closed */ }
    this.pc = null;
    this.el.srcObject = null;
  }

  close(): void {
    this.on = false;
    this.teardown();
  }
}
